import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Screen from '../../components/common/Screen';
import Title from '../../components/common/Title';
import NavBar from '../../components/common/NavBar';
import Icon from '../../components/common/Icon';
import Button from '../../components/elements/Button';
import ImageInput from '../../components/forms/ImageInput';
import Toast from '../../components/forms/Toast';
import { apiRequest, handleImageUpload, teamPhoto } from '../../utils';

const userFallback = require('./../../assets/images/fallback/user.png');

// Meet the Ladies: the ladies who signed up alone find each other. Every card is a team that
// is open to merge — a lady on her own, or friends with room left. "Connect" sends a merge
// request with a personal line; saying yes makes them one team (tickets move along). The
// rules live server-side (routes/meet_the_ladies.py); this page only shows and asks.

const chip = (active) => ({
  border: '1px solid var(--bs-primary, #e83e8c)',
  background: active ? 'var(--bs-primary, #e83e8c)' : 'transparent',
  color: active ? '#fff' : 'var(--bs-primary, #e83e8c)',
  borderRadius: 999, padding: '0.2rem 0.7rem', fontSize: '0.85rem', cursor: 'pointer',
});

const Page = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [connectTo, setConnectTo] = useState(null);
  const [note, setNote] = useState('');
  const [filters, setFilters] = useState({ area: '', lang: '', age: '', level: '' });
  const [uploadError, setUploadError] = useState(null);
  const [showUploadToast, setShowUploadToast] = useState(false);

  const langName = useMemo(() => {
    let dn = null;
    try { dn = new Intl.DisplayNames([i18n.language || 'en'], { type: 'language' }); } catch (_) { dn = null; }
    return (code) => (dn ? dn.of(code) : code);
  }, [i18n.language]);

  const reload = () => {
    apiRequest('GET', '/meet-the-ladies')
      .then((d) => {
        setData(d);
        if (d?.me) {
          setForm({
            age_group: d.me.age_group || '', level: d.me.level || '', area: d.me.area || '',
            languages: d.me.languages || [], interests: d.me.interests || '',
            about: d.me.about || '', is_visible: d.me.is_visible !== false,
          });
          // A solo lady with an unfinished card starts in the editor.
          if (d.me.is_solo && d.me.missing?.length) setEditing(true);
        }
      })
      .catch(() => setData({ eligible: false }));
  };
  useEffect(reload, []);

  const act = async (url, body, doneKey, after) => {
    setBusy(true);
    try {
      const res = await apiRequest('POST', url, body);
      if (doneKey) setToast(t(doneKey));
      if (after) await after(res);
      reload();
    } catch (e) {
      setToast(t(e.message || 'somethingWentWrong'));
    } finally {
      setBusy(false);
    }
  };

  const saveProfile = () => act('/meet-the-ladies/profile', form, 'profileSaved', async () => setEditing(false));

  // After a yes the caller may now sit on the OTHER team (the bigger one survives), so her
  // token is re-minted before anything else loads.
  const accept = (req) => act('/meet-the-ladies/accept', { request_id: req.request_id }, 'mergeDone',
    async () => { try { await apiRequest('POST', '/refresh-team'); } catch (_) { /* stale token is redirected anyway */ } });

  if (!data) {
    return (
      <Screen color='white' p='3'>
        <div className='top'><Title>{t('meetTheLadies')}</Title><p className='text-secondary'>{t('loading')}</p></div>
        <div className='bottom'><NavBar /></div>
      </Screen>
    );
  }

  if (!data.eligible) {
    return (
      <Screen color='white' p='3'>
        <div className='top'>
          <Title>{t('meetTheLadies')}</Title>
          <div className='alert alert-secondary py-2'>{t('mergeFriendsOnly')}</div>
          <Button className='mt-2' onClick={() => navigate('/')}>{t('backToHome')}</Button>
        </div>
        <div className='bottom'><NavBar /></div>
      </Screen>
    );
  }

  const { me, my_team: myTeam, options } = data;
  const isCaptain = myTeam.is_captain;
  // Solo = she chose "I'm coming on my own". A captain who booked one ticket is filling her
  // team: she gets the "show our team" switch, never a personal card.
  const alone = !!me.is_solo;
  const fits = (players) => !data.cap || myTeam.players + players <= data.cap;
  const pendingTo = new Set((data.outgoing || []).map((r) => String(r.team_id).toLowerCase()));

  const match = (c) => {
    if (filters.area && !(c.area || '').toLowerCase().includes(filters.area.toLowerCase())) return false;
    if (filters.lang && !(c.languages || []).includes(filters.lang)) return false;
    if (filters.age && c.age_group !== filters.age) return false;
    if (filters.level && c.level !== filters.level) return false;
    return true;
  };
  const ladies = (data.ladies || []).filter(match);
  const groups = (data.groups || []).filter((g) => !filters.area
    || g.members.some((m) => (m.area || '').toLowerCase().includes(filters.area.toLowerCase())));
  // A filter only earns its place once there is a crowd to sift: with a handful of cards it
  // mostly hides ladies who have not filled in that field yet.
  const showFilters = (data.ladies || []).length + (data.groups || []).length >= 6;
  const filtering = Object.values(filters).some(Boolean);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleLang = (code) => setForm({
    ...form,
    languages: form.languages.includes(code) ? form.languages.filter((c) => c !== code) : [...form.languages, code],
  });

  // Our own team has no seat left: nobody on the board can join us, so no card offers Connect.
  const teamFull = myTeam.spots != null && myTeam.spots <= 0;

  const connectButton = (card, players) => {
    if (!isCaptain || teamFull) return null;
    const key = String(card.team_id).toLowerCase();
    if (pendingTo.has(key)) {
      return <span className='badge bg-light text-secondary'>{t('connectPending')}</span>;
    }
    // A disabled button without a reason reads as "she is taken" — say what is in the way.
    if (!fits(players)) {
      return <span className='text-secondary' style={{ fontSize: '0.8rem' }}>{t('meetTooBigTogether', { count: myTeam.spots })}</span>;
    }
    return (
      <button className='btn btn-sm btn-primary' disabled={busy}
              onClick={() => { setNote(''); setConnectTo(card); }}>
        <Icon name='heart' className='me-1' theme='light' />{t('connect')}
      </button>
    );
  };

  const ladyFacts = (c) => [
    c.age_group,
    c.area,
    c.level ? t(`soloLevel_${c.level}`) : null,
    (c.languages || []).map(langName).join(', ') || null,
  ].filter(Boolean).join(' · ');

  return (
    <Screen color='white' p='3'>
      <div className='top'>
        <Title>{t('meetTheLadies')}</Title>
        <p className='text-secondary' style={{ fontSize: '0.9rem' }}>{t(alone ? 'meetIntro' : 'meetIntroTeam')}</p>

        {/* Why there is no Connect on the cards — up front, not at the bottom of the list. */}
        {!alone && teamFull && (
          <div className='alert alert-secondary py-2'>{t('meetTeamFull', { players: myTeam.players, cap: data.cap })}</div>
        )}
        {!alone && !teamFull && !isCaptain && (
          <div className='alert alert-secondary py-2'>
            {myTeam.captain_name ? t('meetAskCaptain', { name: myTeam.captain_name }) : t('meetCaptainConnects')}
          </div>
        )}

        {toast && (
          <div className='alert alert-info py-2' role='alert' onClick={() => setToast(null)}>{toast}</div>
        )}
        <Toast message={uploadError} showToast={showUploadToast} setShowToast={setShowUploadToast} />

        {/* ---------- My card (a solo lady) / our team on the board (every other team) ---------- */}
        {alone ? (
          <div className='card mb-3'>
            <div className='card-body'>
              <div className='d-flex align-items-center gap-3'>
                <img src={me.photo || userFallback} alt='' style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: '50%' }} />
                <div className='flex-grow-1'>
                  <strong>{t('meetMyCard')}</strong>
                  <div className='text-secondary' style={{ fontSize: '0.85rem' }}>
                    {me.missing?.length ? t('meetCardIncomplete') : (me.is_visible ? t('meetCardVisible') : t('meetCardHidden'))}
                  </div>
                </div>
                {!editing && (
                  <button className='btn btn-sm btn-outline-primary' onClick={() => setEditing(true)}>{t('edit')}</button>
                )}
              </div>

              {editing && form && (
                <div className='mt-3'>
                  <div className='d-flex justify-content-center mb-3'>
                    <ImageInput
                      onUpload={(file) => handleImageUpload(file, '/api/upload-user-image', setUploadError, setShowUploadToast)}
                      caption={t('editPicture')}
                      url={me.photo || userFallback}
                    />
                  </div>

                  <label className='form-label mb-1'>{t('soloAgeGroup')}</label>
                  <div className='d-flex flex-wrap gap-2 mb-3'>
                    {options.age_groups.map((a) => (
                      <span key={a} role='button' tabIndex={0} style={chip(form.age_group === a)}
                            onClick={() => setForm({ ...form, age_group: a })}>{a}</span>
                    ))}
                  </div>

                  <label className='form-label mb-1'>{t('soloArea')}</label>
                  <input className='form-control mb-3' value={form.area} maxLength={60}
                         placeholder={t('soloAreaPlaceholder')} onChange={set('area')} />

                  <label className='form-label mb-1'>{t('soloLanguages')}</label>
                  <div className='d-flex flex-wrap gap-2 mb-3'>
                    {options.languages.map((c) => (
                      <span key={c} role='button' tabIndex={0} style={chip(form.languages.includes(c))}
                            onClick={() => toggleLang(c)}>{langName(c)}</span>
                    ))}
                  </div>

                  <label className='form-label mb-1'>{t('soloLevel')}</label>
                  <div className='d-flex flex-wrap gap-2 mb-3'>
                    {options.levels.map((l) => (
                      <span key={l} role='button' tabIndex={0} style={chip(form.level === l)}
                            onClick={() => setForm({ ...form, level: l })}>{t(`soloLevel_${l}`)}</span>
                    ))}
                  </div>

                  <label className='form-label mb-1'>{t('soloInterests')}</label>
                  <input className='form-control mb-3' value={form.interests} maxLength={300}
                         placeholder={t('soloInterestsPlaceholder')} onChange={set('interests')} />

                  <label className='form-label mb-1'>{t('soloAbout')}</label>
                  <textarea className='form-control mb-3' rows={2} value={form.about} maxLength={300}
                            placeholder={t('soloAboutPlaceholder')} onChange={set('about')} />

                  <div className='form-check form-switch mb-3'>
                    <input className='form-check-input' type='checkbox' id='soloVisible'
                           checked={form.is_visible} onChange={(e) => setForm({ ...form, is_visible: e.target.checked })} />
                    <label className='form-check-label' htmlFor='soloVisible'>{t('soloVisible')}</label>
                  </div>
                  <p className='text-secondary' style={{ fontSize: '0.8rem' }}>
                    <Icon name='lock' className='me-1' />{t('soloPrivacy')}
                  </p>
                  <button className='btn btn-primary w-100' disabled={busy} onClick={saveProfile}>{t('save')}</button>
                </div>
              )}
            </div>
          </div>
        ) : !teamFull && (
          <div className='form-check form-switch mb-3'>
            <input className='form-check-input' type='checkbox' id='teamOnBoard'
                   checked={!!myTeam.open} disabled={busy || !isCaptain || myTeam.spots === 0}
                   onChange={(e) => act('/meet-the-ladies/open', { open: e.target.checked }, e.target.checked ? 'openToMergeOn' : 'openToMergeOff')} />
            <label className='form-check-label' htmlFor='teamOnBoard'>
              {myTeam.spots != null ? t('meetTeamOnBoard', { count: myTeam.spots }) : t('openToMerge')}
            </label>
            {!isCaptain && <div className='text-secondary' style={{ fontSize: '0.85rem' }}>{t('captainOnly')}</div>}
          </div>
        )}

        {/* ---------- Somebody pressed Connect on us ---------- */}
        {data.incoming?.length > 0 && (
          <>
            <h5>{t('meetIncoming')}</h5>
            {data.incoming.map((r) => (
              <div key={r.request_id} className='card mb-2 border-primary'>
                <div className='card-body py-2'>
                  <div className='d-flex align-items-center gap-2'>
                    <img src={r.kind === 'group' ? teamPhoto(r.photo) : (r.photo || userFallback)} alt=''
                         style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: r.kind === 'group' ? 8 : '50%' }} />
                    <div className='flex-grow-1'>
                      <strong>{r.kind === 'group' ? r.name : r.first_name}</strong>
                      <div className='text-secondary' style={{ fontSize: '0.8rem' }}>
                        {r.kind === 'group' ? t('playersCount', { count: r.players }) : ladyFacts(r)}
                      </div>
                    </div>
                  </div>
                  {r.note && <p className='mt-2 mb-2 fst-italic' style={{ fontSize: '0.9rem' }}>“{r.note}”</p>}
                  {isCaptain ? (
                    <div className='d-flex gap-2 mt-2'>
                      <button className='btn btn-sm btn-success' disabled={busy}
                              onClick={() => window.confirm(t('meetAcceptConfirm', { name: r.kind === 'group' ? r.name : r.first_name })) && accept(r)}>
                        {t('meetAccept')}
                      </button>
                      <button className='btn btn-sm btn-outline-secondary' disabled={busy}
                              onClick={() => act('/meet-the-ladies/decline', { request_id: r.request_id }, 'mergeDeclined')}>
                        {t('decline')}
                      </button>
                    </div>
                  ) : (
                    <div className='text-secondary' style={{ fontSize: '0.85rem' }}>{t('mergeIncomingWaitCaptain')}</div>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        {/* ---------- Our pending Connects ---------- */}
        {data.outgoing?.length > 0 && (
          <>
            <h6 className='mt-2'>{t('meetOutgoing')}</h6>
            {data.outgoing.map((r) => (
              <div key={r.request_id} className='d-flex align-items-center justify-content-between mb-2'>
                <span>{r.kind === 'group' ? r.name : r.first_name}</span>
                {isCaptain && (
                  <button className='btn btn-sm btn-link text-secondary' disabled={busy}
                          onClick={() => act('/meet-the-ladies/cancel', { request_id: r.request_id }, 'mergeCancelled')}>
                    {t('cancel')}
                  </button>
                )}
              </div>
            ))}
          </>
        )}

        {/* ---------- Filters ---------- */}
        {showFilters && (
        <div className='d-flex flex-wrap gap-2 mt-3 mb-2'>
          <input className='form-control form-control-sm' style={{ maxWidth: 170 }} value={filters.area}
                 placeholder={t('soloArea')} onChange={(e) => setFilters({ ...filters, area: e.target.value })} />
          <select className='form-select form-select-sm' style={{ maxWidth: 150 }} value={filters.lang}
                  onChange={(e) => setFilters({ ...filters, lang: e.target.value })}>
            <option value=''>{t('soloLanguages')}</option>
            {options.languages.map((c) => <option key={c} value={c}>{langName(c)}</option>)}
          </select>
          <select className='form-select form-select-sm' style={{ maxWidth: 120 }} value={filters.age}
                  onChange={(e) => setFilters({ ...filters, age: e.target.value })}>
            <option value=''>{t('soloAgeGroup')}</option>
            {options.age_groups.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select className='form-select form-select-sm' style={{ maxWidth: 150 }} value={filters.level}
                  onChange={(e) => setFilters({ ...filters, level: e.target.value })}>
            <option value=''>{t('soloLevel')}</option>
            {options.levels.map((l) => <option key={l} value={l}>{t(`soloLevel_${l}`)}</option>)}
          </select>
        </div>
        )}

        {/* ---------- The ladies ---------- */}
        <h5 className='mt-2'>{t('meetLadiesHeading')}</h5>
        {ladies.length === 0 && (
          <p className='text-secondary'>{t(filtering && data.ladies?.length ? 'meetNoMatch' : 'meetNoLadies')}</p>
        )}
        <div className='row g-2'>
          {ladies.map((c) => (
            <div key={c.team_id} className='col-12 col-sm-6'>
              <div className='card h-100'>
                <div className='card-body d-flex gap-3'>
                  <img src={c.photo || userFallback} alt=''
                       style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: '50%', flexShrink: 0 }} />
                  <div className='flex-grow-1' style={{ minWidth: 0 }}>
                    <strong>{c.first_name}</strong>
                    <div className='text-secondary' style={{ fontSize: '0.8rem' }}>{ladyFacts(c)}</div>
                    {c.about && <div style={{ fontSize: '0.85rem' }} className='mt-1'>{c.about}</div>}
                    {c.interests && <div className='text-secondary mt-1' style={{ fontSize: '0.8rem' }}>♡ {c.interests}</div>}
                    <div className='mt-2'>{connectButton(c, 1)}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ---------- Friends with room ---------- */}
        {groups.length > 0 && (
          <>
            <h5 className='mt-3'>{t('meetGroupsHeading')}</h5>
            {groups.map((g) => (
              <div key={g.team_id} className='card mb-2'>
                <div className='card-body d-flex align-items-center gap-3 py-2'>
                  <img src={teamPhoto(g.photo)} alt='' style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8 }} />
                  <div className='flex-grow-1' style={{ minWidth: 0 }}>
                    <strong>{g.name}</strong>
                    <div className='text-secondary' style={{ fontSize: '0.8rem' }}>
                      {g.members.map((m) => m.first_name).join(', ')}
                      {g.spots != null && <> · {t('meetSpotsLeft', { count: g.spots })}</>}
                    </div>
                  </div>
                  {connectButton(g, g.players)}
                </div>
              </div>
            ))}
          </>
        )}

        <Button className='mt-3' onClick={() => navigate('/')}>{t('backToHome')}</Button>

        {/* ---------- Connect: one personal line ---------- */}
        {connectTo && (
          <div onClick={() => setConnectTo(null)}
               style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: '1rem' }}>
            <div onClick={(e) => e.stopPropagation()}
                 style={{ background: '#fff', borderRadius: 14, padding: '1.25rem', maxWidth: 420, width: '100%', boxShadow: '0 10px 40px -10px rgba(0,0,0,0.4)' }}>
              <h5 className='mb-2'>{t('connectWith', { name: connectTo.kind === 'group' ? connectTo.name : connectTo.first_name })}</h5>
              <p className='text-secondary mb-2' style={{ fontSize: '0.9rem' }}>{t('connectNoteHint')}</p>
              <textarea className='form-control' rows={3} maxLength={500} value={note}
                        placeholder={t('connectNotePlaceholder')} onChange={(e) => setNote(e.target.value)} />
              <div className='d-flex gap-2 justify-content-end mt-3'>
                <button className='btn btn-outline-secondary' disabled={busy} onClick={() => setConnectTo(null)}>{t('cancel')}</button>
                <button className='btn btn-primary' disabled={busy}
                        onClick={() => { const c = connectTo; setConnectTo(null); act('/meet-the-ladies/connect', { team_id: c.team_id, note }, 'connectSent'); }}>
                  <Icon name='paper-plane' className='me-1' theme='light' />{t('connectSend')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className='bottom'>
        <NavBar />
      </div>
    </Screen>
  );
};

export default Page;
